import { useCallback, useMemo } from 'react';
import { useMatches, useNavigate, useParams } from 'react-router';
import { PROJECT_SECTIONS } from '@/entities/project';
import { ROUTES } from '@/shared/config/routes';

/**
 * Which section the current URL is in, and how to move between them.
 *
 * Deliberately narrower than the side panel's hook: that one also fed a project
 * switcher, which the tab bar does not carry.
 */
export function useProjectDetailTabs() {
  const navigate = useNavigate();
  const matches = useMatches();
  const { platformProjectId } = useParams<{ platformProjectId: string }>();

  const activeSection = useMemo(() => {
    // Route handle metadata first, so a nested route such as a folder detail
    // still resolves to the section that owns it rather than to no tab at all.
    for (let i = matches.length - 1; i >= 0; i--) {
      const handle = matches[i].handle as { section?: string } | undefined;
      if (handle?.section) {
        return PROJECT_SECTIONS.find((section) => section.id === handle.section);
      }
    }

    return PROJECT_SECTIONS.find((section) =>
      matches.some((match) => match.pathname.endsWith(`/${section.id}`)),
    );
  }, [matches]);

  const handleSectionClick = useCallback(
    (sectionId: string) => {
      if (platformProjectId) {
        void navigate(`${ROUTES.PROJECT_DETAIL(platformProjectId)}/${sectionId}`);
      }
    },
    [platformProjectId, navigate],
  );

  return { activeSection, handleSectionClick };
}
